import { DownloadOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  ConfigProvider,
  Empty,
  Flex,
  Pagination,
  Row,
  Segmented,
  Typography,
  Tag,
} from "antd";
import PageHeading from "components/PageHeading.component";
import StatBand from "components/StatBand.component";
import { getAllProductsByBusinessId } from "hooks/queries/product";
import { useRememberedTablePage } from "hooks/useRememberedTablePage";
import { useTabParam } from "hooks/useTabParam";
import { pillSegmentedTheme } from "pages/analytics/RangeSegmented";
import {
  comparePromoStatus,
  PromoStatus,
  PromoStatusBadge,
  promoStatusOf,
  promoStatusWord,
} from "pages/products/component/PromoStatusBadge";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tnum } from "shared/components/TableText";
import PATHS from "shared/constants/paths";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { AdminCapability } from "shared/utils/capabilities";
import { translate, translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { ProductType } from "shared/types";
import { formatCurrency, formatTierValidity } from "shared/utils";
import { downloadCatalogueCsv } from "shared/utils/catalogue-csv";
import { textPx } from "shared/constants/zoom";

// Band segments (formerly the Reguler/Promo Tabs, plus a Semua view the tabs
// never had). "reguler" stays the default so every fresh visit still shows
// the curated catalog, not the accumulated promo history.
const MEMBERSHIP_VIEWS = ["semua", "reguler", "promo"] as const;

// Deliberately NOT the shared table PAGE_SIZE (10) — this is a 3-column card
// grid (xs=1, sm=2, lg=3 per Col below), and 10 isn't evenly divisible by
// either 2 or 3, so the last page always stranded a lone card on its own
// row. 6 is a multiple of both (2 rows of 3 on desktop, 3 rows of 2 on
// tablet), so every page except a genuinely-partial last one fills evenly —
// more pages than 12 would give, traded for a shorter page each time.
const MEMBERSHIP_PAGE_SIZE = 6;

// Membership products card grid (prototype "MEMBERSHIP" view) — sibling of
// the Paket page under the Produk submenu.
const MembershipPage = () => {
  const navigate = useNavigate();
  const { data, isLoading } = getAllProductsByBusinessId();
  const allMemberships = useMemo(
    () =>
      data?.products.filter((p) => p.product_type === ProductType.Membership) ??
      [],
    [data],
  );

  // Same Semua/Reguler/Promo split as the Paket table, for the same reason:
  // a promo membership never disappears on its own once it ends (is_promo
  // just gets re-tagged "Berakhir"), so without this the grid only grows
  // more mixed over time. Same in-page ?tab= pattern (useTabParam) as
  // packages.tsx, driven by the stat band, not a separate route.
  const { activeKey, onChange: onViewChange } = useTabParam(
    "reguler",
    "tab",
    MEMBERSHIP_VIEWS,
  );
  const regularMemberships = useMemo(
    () => allMemberships.filter((p) => !p.is_promo),
    [allMemberships],
  );
  // Aktif before Mendatang before Berakhir by default, same reasoning and
  // same shared comparator as packages.tsx — within each group, the most
  // urgent/recent item sorts first (see comparePromoStatus).
  const promoMemberships = useMemo(
    () => [...allMemberships].filter((p) => p.is_promo).sort(comparePromoStatus),
    [allMemberships],
  );
  // Promo-tab-only status filter (Semua/Aktif/Mendatang/Berakhir) — the
  // catalog only grows over time since is_promo never clears once a promo
  // ends, so this is how an admin narrows down a Promo tab that's
  // accumulated a long tail of archived promos.
  const [statusFilter, setStatusFilter] = useState<PromoStatus | "all">("all");
  const filteredPromoMemberships = useMemo(
    () =>
      statusFilter === "all"
        ? promoMemberships
        : promoMemberships.filter((p) => promoStatusOf(p) === statusFilter),
    [promoMemberships, statusFilter],
  );
  // Semua lists curated products first, then the promo history in its
  // status order (the promo Segmented filter applies only on the Promo
  // view, so Semua is always unfiltered).
  const memberships =
    activeKey === "promo"
      ? filteredPromoMemberships
      : activeKey === "semua"
        ? [...regularMemberships, ...promoMemberships]
        : regularMemberships;

  // The card grid has no antd Table to give it pagination for free, unlike
  // packages.tsx — wired up manually with the same page-memory hook, one
  // call per tab (NOT one call with a dynamic `membership-${activeKey}`
  // storage key — useRememberedTablePage reads its initial page via
  // useState's lazy initializer, which only runs on mount; since this
  // component doesn't unmount when the tab changes, a dynamic key would
  // silently keep whatever page number was already in that state slot,
  // stranding you on e.g. Promo showing an empty page 2 carried over from
  // Reguler). Two constant-keyed calls give each tab its own real, stable
  // state slot instead.
  const semuaPagination = useRememberedTablePage("membership-semua");
  const regularPagination = useRememberedTablePage("membership-reguler");
  const promoPagination = useRememberedTablePage("membership-promo");
  const { page, handleTableChange } =
    activeKey === "promo"
      ? promoPagination
      : activeKey === "semua"
        ? semuaPagination
        : regularPagination;
  const currentPage = page ?? 1;
  const pagedMemberships = memberships.slice(
    (currentPage - 1) * MEMBERSHIP_PAGE_SIZE,
    currentPage * MEMBERSHIP_PAGE_SIZE,
  );

  // Changing the status filter can shrink the list under the current page
  // (e.g. viewing page 2 of Semua, then narrowing to Aktif) — reset to page
  // 1 the same way antd's own Table does on a filter change (see
  // useRememberedTablePage's doc comment), instead of stranding the grid on
  // an now out-of-range page.
  const onStatusFilterChange = (next: PromoStatus | "all") => {
    setStatusFilter(next);
    handleTableChange({ current: 1 });
  };

  // Pembatasan Admin: creating is a catalogue write (the create route is
  // guarded too).
  const { isRestricted } = useAdminCapabilities();
  const catalogRestricted = isRestricted(AdminCapability.CatalogPricing);

  return (
    <>
      <PageHeading title={translate("membership")} />
      <Flex vertical gap={16}>
        {/* The catalog views, formerly Tabs: each segment shows that slice
            of the grid (re-tap a slice to widen back to Semua). Counts are
            product counts, so the band doubles as the page's timeless
            facts. */}
        <Flex justify="space-between" align="flex-end" gap={16} wrap="wrap">
          <StatBand
            segments={[
              {
                key: "semua",
                value: allMemberships.length,
                label: translate("list_header.all"),
              },
              {
                key: "reguler",
                value: regularMemberships.length,
                label: translate("product_catalog.tab_reguler"),
              },
              {
                key: "promo",
                value: promoMemberships.length,
                label: translate("product_catalog.tab_promo"),
              },
            ]}
            selectedKey={activeKey}
            onSelect={(key) => onViewChange(key === activeKey ? "semua" : key)}
          />
          <Flex gap={8}>
            {/* Same whole-catalogue export as the Paket page: one id_paket
                lookup table regardless of which page it was taken from. */}
            <Button
              icon={<DownloadOutlined />}
              disabled={!data?.products.length}
              onClick={() => downloadCatalogueCsv(data?.products ?? [])}
            >
              {translate("product_catalog.export_catalogue")}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={catalogRestricted}
              onClick={() => navigate(PATHS.MEMBERSHIP_NEW)}
            >
              {translate("product_catalog.add_membership")}
            </Button>
          </Flex>
        </Flex>
        {activeKey === "promo" && (
          // Same pill-shaped Segmented family as ScheduleViewToggle and every
          // Analytics range picker (pillSegmentedTheme), not a bespoke style.
          <ConfigProvider theme={pillSegmentedTheme}>
            <Segmented
              style={{ alignSelf: "flex-start" }}
              value={statusFilter}
              onChange={(value) => onStatusFilterChange(value as PromoStatus | "all")}
              options={[
                { label: translate("product_catalog.promo_status_all"), value: "all" },
                {
                  label: promoStatusWord("active"),
                  value: "active" as PromoStatus,
                },
                {
                  label: promoStatusWord("upcoming"),
                  value: "upcoming" as PromoStatus,
                },
                {
                  label: promoStatusWord("ended"),
                  value: "ended" as PromoStatus,
                },
              ]}
            />
          </ConfigProvider>
        )}
        {memberships.length === 0 && !isLoading ? (
          <Card>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </Card>
        ) : (
          <Row gutter={[16, 16]}>
            {pagedMemberships.map((product) => {
              const tier = product.tiers[0];
              const promoEnded = promoStatusOf(product) === "ended";
              return (
                <Col key={product.id} xs={24} sm={12} lg={8}>
                  <Card
                    hoverable
                    onClick={() => navigate(`${PATHS.MEMBERSHIP}/${product.id}`)}
                    styles={{ body: { padding: 20 } }}
                  >
                    {/* Card header: the name with ONE chip pinned top-right
                        (the promo status badge on a promo card, the validity
                        tag on a regular one; chris, 2026-10-17: a studio runs
                        one promo at a time and knows its term, and two chips
                        beside a long name broke it into three lines); the
                        price directly under the name; then the member counts.
                        The
                        name is clamped to two lines (full name on hover) and
                        the whole header reserves the height of a two-line name
                        plus the price, so every card shares one height
                        whatever row, page or filter it lands in: a one-line
                        name leaves its spare line BELOW the price, never
                        between the name and the price (chris). wrap="nowrap"
                        + flex:1/minWidth:0 keeps the badge a fixed flex item:
                        a long name wraps inside its own column instead of
                        pushing the badge down. */}
                    <div style={{ minHeight: textPx(15) * 3 + 6 + textPx(18) * 1.5 + 14 }}>
                      <Flex justify="space-between" align="flex-start" gap={8} wrap="nowrap">
                        <Typography.Text
                          strong
                          title={product.name}
                          style={{
                            fontSize: textPx(15),
                            lineHeight: 1.5,
                            flex: 1,
                            minWidth: 0,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            overflowWrap: "anywhere",
                            color: promoEnded ? colors.ink3 : undefined,
                          }}
                        >
                          {product.name}
                        </Typography.Text>
                        {product.is_promo ? (
                          // flexShrink wrapper: the badge is a plain span and
                          // would otherwise compress/wrap before the name does.
                          <span style={{ flexShrink: 0 }}>
                            <PromoStatusBadge product={product} />
                          </span>
                        ) : (
                          tier && (
                            // The same metrics as the status badge in the
                            // promo card's slot (11px semibold, 3x9 padding,
                            // 16px line), so the two chips are one size.
                            <Tag
                              style={{
                                background: colors.bgSoft,
                                color: colors.ink2,
                                borderColor: "transparent",
                                borderRadius: 6,
                                marginInlineEnd: 0,
                                flexShrink: 0,
                                padding: "3px 9px",
                                fontSize: textPx(11),
                                fontWeight: 600,
                                lineHeight: `${textPx(16)}px`,
                              }}
                            >
                              {translate("product_catalog.valid_for")} {formatTierValidity(tier)}
                            </Tag>
                          )
                        )}
                      </Flex>
                      <Typography.Text
                        strong
                        style={{
                          display: "block",
                          fontSize: textPx(18),
                          lineHeight: 1.5,
                          marginTop: 6,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {tier ? formatCurrency(tier.price) : "-"}
                      </Typography.Text>
                    </div>
                    <Flex
                      vertical
                      gap={0}
                      style={{ borderTop: `1px solid ${colors.line}` }}
                    >
                      <Flex justify="space-between" style={{ padding: "8px 0" }}>
                        <Typography.Text
                          style={{ color: colors.ink2, fontSize: textPx(13) }}
                        >
                          {/* Memberships, not people: the product page's
                              Membership Aktif tab counts the same rows. */}
                          {translate("product_holders.title_membership")}
                        </Typography.Text>
                        <Typography.Text style={{ fontSize: textPx(13), ...tnum }}>
                          {product.active_member_count ?? 0}
                        </Typography.Text>
                      </Flex>
                      <Flex
                        justify="space-between"
                        style={{
                          padding: "8px 0",
                          borderTop: `1px solid ${colors.line}`,
                        }}
                      >
                        <Typography.Text
                          style={{ color: colors.ink2, fontSize: textPx(13) }}
                        >
                          {/* The studio's expiry alert window (an older
                              server sent no number and counted 14 days). */}
                          {translateWithVars(
                            (product.expiring_soon_days ?? 14) === 1
                              ? "product_catalog.expiring_within_one"
                              : "product_catalog.expiring_within_other",
                            { n: product.expiring_soon_days ?? 14 },
                          )}
                        </Typography.Text>
                        <Typography.Text style={{ fontSize: textPx(13), ...tnum }}>
                          {product.expiring_soon_count ?? 0}
                        </Typography.Text>
                      </Flex>
                    </Flex>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
        {memberships.length > MEMBERSHIP_PAGE_SIZE && (
          <Flex justify="flex-end">
            <Pagination
              current={currentPage}
              pageSize={MEMBERSHIP_PAGE_SIZE}
              total={memberships.length}
              showSizeChanger={false}
              onChange={(nextPage) => handleTableChange({ current: nextPage })}
            />
          </Flex>
        )}
      </Flex>
    </>
  );
};

export default MembershipPage;
